import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Sparkles } from 'lucide-react'
import { contentAPI } from '../../services/contentAPI'

const HeroSection = () => {
  const [heroContent, setHeroContent] = useState({
    title: 'Transform Your Customer Service with AI Agents',
    subtitle: 'Deploy intelligent AI chatbots and voice calling agents that work 24/7. Automate support, boost engagement, and scale your business effortlessly.',
    cta_text: 'Get Started Free',
    cta_link: '/pricing',
    secondary_cta_text: 'Watch Demo',
    background_image: ''
  })
  
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    loadHeroContent()
    setIsVisible(true)
  }, [])

  const loadHeroContent = async () => {
    try {
      const response = await contentAPI.getHeroContent()
      setHeroContent(response.data)
    } catch (error) {
      console.log('Using default hero content')
    }
  }

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-white via-gray-50 to-white">
      {/* Subtle background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-pink-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 relative z-10 w-full">
        <div className={`max-w-4xl mx-auto text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-8 animate-fade-in">
            <Sparkles className="w-4 h-4" />
            <span>AI-Powered Customer Service Platform</span>
          </div>
          
          {/* Main Headline */}
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold text-gray-900 mb-8 leading-tight tracking-tight">
            {heroContent.title}
          </h1>
          
          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-gray-600 mb-12 leading-relaxed max-w-3xl mx-auto font-normal">
            {heroContent.subtitle}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
            <Link 
              to={heroContent.cta_link}
              className="group inline-flex h-14 px-8 bg-gray-900 text-white rounded-xl hover:bg-gray-800 items-center justify-center gap-2 transition-all duration-200 font-semibold text-base shadow-lg hover:shadow-xl"
            >
              {heroContent.cta_text}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            
            <Link 
              to="/demo"
              className="inline-flex h-14 px-8 bg-white text-gray-900 rounded-xl border border-gray-200 hover:border-gray-300 items-center justify-center gap-2 transition-all duration-200 font-semibold text-base hover:bg-gray-50"
            >
              {heroContent.secondary_cta_text}
            </Link>
          </div>

          {/* Social Proof - Simple Stats */}
          <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                <div className="w-8 h-8 rounded-full bg-blue-500 border-2 border-white"></div>
                <div className="w-8 h-8 rounded-full bg-purple-500 border-2 border-white"></div>
                <div className="w-8 h-8 rounded-full bg-pink-500 border-2 border-white"></div>
              </div>
              <span className="font-medium">1000+ active users</span>
            </div>
            <div className="h-4 w-px bg-gray-300"></div>
            <div className="font-medium">99.9% uptime</div>
            <div className="h-4 w-px bg-gray-300"></div>
            <div className="font-medium">24/7 support</div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default HeroSection